/**
 * Set Default Payment Method Mutation Hook
 *
 * Sets one of a user's stored cards as their default (the card that bills their
 * membership). Used by the account-setup page so an imported member can choose
 * their default pre-login.
 *
 * The service is data-or-throw (canonical `apiClient`); on success we invalidate
 * the account-setup **user detail** query (`queryKeys.tenant.userDetail`) — that's
 * the query whose embedded `paymentMethods` list backs the page, so it re-renders
 * with the new default. (Note: NOT `queryKeys.tenant.paymentMethods`, which the
 * account-setup page doesn't read.)
 *
 * @module payment/hooks
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '~/lib/query/keys';
import { paymentMethodService } from '~/services/internalApi/payment/api/paymentMethodService';

export interface UseSetDefaultPaymentMethodOptions {
  /** Tenant handle — sends `X-Tenant-Id` on the request AND scopes cache invalidation. */
  tenantHandle: string | undefined;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

/**
 * Mutation to set a member's default payment method.
 *
 * @example
 * ```tsx
 * const setDefault = useSetDefaultPaymentMethod({ tenantHandle });
 * await setDefault.mutateAsync({ userId, paymentMethodId });
 * ```
 */
export function useSetDefaultPaymentMethod(options: UseSetDefaultPaymentMethodOptions) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      paymentMethodId,
    }: {
      userId: number;
      paymentMethodId: number | string;
    }) => paymentMethodService.setDefaultPaymentMethod(userId, paymentMethodId, options.tenantHandle),

    onSuccess: (_data, variables) => {
      if (options.tenantHandle) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.tenant.userDetail(options.tenantHandle, variables.userId),
        });
      }
      options?.onSuccess?.();
    },

    onError: (error: Error) => {
      console.error('[useSetDefaultPaymentMethod] Error:', error);
      options?.onError?.(error);
    },
  });
}
